import {
  Avatar,
  Chip,
  Link,
  List,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import type { ImportItem } from "../../../../api/types";
import { DateTimeDisplay } from "../../../../components/DateTimeDisplay";
import { DateDisplay } from "../../../../components/DateDisplay";
import { CopyableText } from "../../../../components/CopyableText";
import { AdminListRow } from "../../../../components/AdminList";
import { useRegistry } from "../../../../context/RegistryContext";
import { ImportActions } from "./ImportActions";

export type ImportListViewProps = {
  imports: ImportItem[];
  onEdit: (item: ImportItem) => void;
  onViewTransactions: (item: ImportItem) => void;
  onViewCsv: (item: ImportItem) => void;
  onOpenDiff: (item: ImportItem) => void;
  onRerun: (item: ImportItem) => void;
  rerunningId: string | null;
  onDelete?: (item: ImportItem) => void;
  onRestore?: (item: ImportItem) => void;
  onClearFinalized?: (item: ImportItem) => void;
};

export const ImportListView = ({
  imports,
  onEdit,
  onViewTransactions,
  onViewCsv,
  onOpenDiff,
  onRerun,
  rerunningId,
  onDelete,
  onRestore,
  onClearFinalized,
}: ImportListViewProps) => {
  const { invoiceMap } = useRegistry();
  return (
    <Paper variant="outlined">
      <List sx={{ width: "100%", bgcolor: "background.paper" }}>
        {imports.map((item, index) => {
          const invoiceName = item.invoiceId
            ? (invoiceMap[item.invoiceId]?.name ?? item.invoiceId)
            : null;
          return (
            <AdminListRow
              key={item.id}
              divider={index < imports.length - 1}
              avatar={
                <Avatar variant="rounded">
                  <DescriptionIcon />
                </Avatar>
              }
              primary={
                <Link
                  component="button"
                  type="button"
                  variant="body1"
                  onClick={() => onEdit(item)}
                  aria-label={`Edit import ${item.importerName}`}
                  underline="hover"
                  sx={{
                    textAlign: "left",
                    color: "text.primary",
                    fontWeight: 500,
                    minHeight: 32,
                  }}
                >
                  {item.importerName}
                </Link>
              }
              secondary={
                <Stack component="span" spacing={0.25}>
                  <Stack
                    direction="row"
                    spacing={2}
                    component="span"
                    sx={{ flexWrap: "wrap" }}
                  >
                    <DateTimeDisplay date={item.dateImported} />
                    {item.firstTransactionDate && item.lastTransactionDate ? (
                      <Typography component="span" variant="body2">
                        <DateDisplay date={item.firstTransactionDate} />
                        {" – "}
                        <DateDisplay date={item.lastTransactionDate} />
                      </Typography>
                    ) : null}
                    <Typography component="span" variant="body2">
                      Invoice: {invoiceName ?? "None"}
                    </Typography>
                    {item.finalizedAt && (
                      <Tooltip title="Filled from the period's invoice, which is the complete list for it. Statement files are refused for this period until this is cleared on the import.">
                        <Chip
                          component="span"
                          size="small"
                          color="success"
                          variant="outlined"
                          label="Finalized"
                        />
                      </Tooltip>
                    )}
                  </Stack>
                  <Typography component="span" variant="body2">
                    <CopyableText text={item.id} maxLength={10} />
                  </Typography>
                </Stack>
              }
              actions={
                <ImportActions
                  item={item}
                  onEdit={onEdit}
                  onViewTransactions={onViewTransactions}
                  onViewCsv={onViewCsv}
                  onOpenDiff={onOpenDiff}
                  onRerun={onRerun}
                  isRerunning={rerunningId === item.id}
                  onDelete={onDelete}
                  onRestore={onRestore}
                  onClearFinalized={onClearFinalized}
                />
              }
            />
          );
        })}
      </List>
    </Paper>
  );
};
